import { useState } from "react";

import {
  faEdit,
  faFlask,
  faRefresh,
  faWavePulse,
} from "@fortawesome/pro-regular-svg-icons";
import { noop } from "lodash";
import { DateRange, SelectRangeEventHandler } from "react-day-picker";
import { useNavigate } from "react-router-dom";

import { EllipsisOptionsDropdown } from "src/base-components/OptionsDropdown/EllipsisOptionsDropdown";
import { OptionsDropdownElement } from "src/base-components/OptionsDropdown/OptionsDropdownItems";
import { EventConfigEnriched } from "src/clients/features-control";
import { EventsFilterDropdown } from "src/eventsCatalogue/EventsFilterDropdown";
import { EventFilterFormData } from "src/eventsCatalogue/filters/types";
import { UrlFilterParams } from "src/eventsCatalogue/hooks/useEventFiltersUrl";
import { getEventIcon } from "src/eventsCatalogue/utils";
import { useEnvironment } from "src/hooks/useEnvironment";
import { useWorkspaceFeatureGates } from "src/hooks/useWorkspaceFeatureGates";
import {
  SubHeader,
  SubHeaderButton,
  SubHeaderDatePicker,
  SubHeaderSearchBox,
  SubHeaderTab,
  SubHeaderTabs,
} from "src/layout/SubHeader";
import { EventsCatalogueParams, getUrlToEventsPage } from "src/router/urls";
import { useParamsDecode } from "src/utils/useParamsDecode";

export const EventsEllipsisSwitchEnvironment: React.FC = () => {
  const [environment, setEnvironment] = useEnvironment();
  const dropdownItem: OptionsDropdownElement = {
    key: `Switch to ${environment === "sandbox" ? "Live" : "Sandbox"}`,
    icon: environment === "sandbox" ? faWavePulse : faFlask,
    action: () =>
      setEnvironment(environment === "sandbox" ? "live" : "sandbox"),
  };
  return (
    <span className="h-full w-[49px] border-gray-200 border-l p-3.5">
      <EllipsisOptionsDropdown
        elements={[dropdownItem]}
        iconSize="sm"
        placement="bottom start"
      />
    </span>
  );
};

type EventsSubHeaderProps = {
  eventTypes: EventConfigEnriched[];
  dateRangePickerValue?: DateRange | undefined;
  onTimeWindowChange?: SelectRangeEventHandler;
  onRefresh: () => void;
  isRefreshing?: boolean;
  onSearchQueryChange: (query: string) => void;
  searchQuery?: string | null;
  onClearFilters: () => void;
  onApplyFilters: (
    formData: EventFilterFormData,
    dateRange: DateRange | undefined,
  ) => void;
  hasFilterCondition?: boolean;
  urlFilterParams?: UrlFilterParams | null;
};

export const EventsSubHeader: React.FC<EventsSubHeaderProps> = ({
  eventTypes,
  dateRangePickerValue,
  onClearFilters,
  hasFilterCondition,
  onTimeWindowChange,
  onRefresh,
  isRefreshing,
  searchQuery,
  onSearchQueryChange,
  onApplyFilters,
  urlFilterParams,
}) => {
  const {
    orgId,
    wsId,
    event_type: eventType,
  } = useParamsDecode<EventsCatalogueParams>();
  const navigate = useNavigate();
  const { featuresEventsEnabled } = useWorkspaceFeatureGates();
  const [isFilterDropdownOpen, setIsFilterDropdownOpen] = useState(false);
  const hasEventTypes = eventTypes.length > 0;

  return (
    <SubHeader
      leftSlot={
        <SubHeaderTabs dataLoc="events-subheader-tabs">
          {hasEventTypes &&
            eventTypes.filter(Boolean).map((eventType) => (
              <SubHeaderTab
                key={eventType.event_type}
                icon={getEventIcon(eventType)}
                to={getUrlToEventsPage({
                  orgId,
                  wsId,
                  eventType: eventType.event_type,
                })}
                rightSlot={
                  <EllipsisOptionsDropdown
                    elements={[
                      {
                        key: "Edit Schema",
                        icon: faEdit,
                        action: () =>
                          navigate(
                            getUrlToEventsPage({
                              orgId,
                              wsId,
                              eventType: eventType.event_type + "/edit",
                            }),
                          ),
                      },
                    ]}
                    iconSize="xs"
                    placement="bottom start"
                  />
                }
              >
                {eventType.display_name_plural}
              </SubHeaderTab>
            ))}
        </SubHeaderTabs>
      }
    >
      {hasEventTypes && (
        <>
          <SubHeaderSearchBox
            defaultValue={searchQuery ?? ""}
            placeholder="Search by event ID"
            onChange={onSearchQueryChange}
          />
          {onTimeWindowChange && (
            <SubHeaderDatePicker
              disabled={isFilterDropdownOpen}
              suffixIcon={!isFilterDropdownOpen}
              usePredefinedDates={!isFilterDropdownOpen}
              value={dateRangePickerValue}
              onChange={onTimeWindowChange}
            />
          )}
          {featuresEventsEnabled && (
            <EventsFilterDropdown
              dateRangeValue={dateRangePickerValue}
              eventType={eventType}
              hasFilterCondition={hasFilterCondition}
              isOpen={isFilterDropdownOpen}
              urlFilterParams={urlFilterParams}
              onApplyFilters={onApplyFilters}
              onClearFilters={onClearFilters}
              onDateRangeChange={onTimeWindowChange ?? noop}
              onOpenChange={setIsFilterDropdownOpen}
            />
          )}
          <SubHeaderButton
            icon={faRefresh}
            spin={isRefreshing}
            tooltip="Refresh"
            onClick={isRefreshing ? noop : onRefresh}
          />
        </>
      )}
    </SubHeader>
  );
};
